// src/components/Sidebar.jsx
import React, { useState } from "react";
import {
  Home,
  Activity,
  LineChart,
  Settings,
  ShieldCheck,
  LibraryBig,
  BookOpenCheck,
  Repeat,
  WalletCards,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useProfile } from "context/ProfileContext";
import { useAppAccess } from "context/AppAccessContext";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { supabase } from "services/supabaseClient";
import { logAudit } from "lib/audit";
import { useDialog } from "context/DialogContext";
import { clearActivityState } from "lib/sessionActivity";

export default function Sidebar({ collapsed, setCollapsed }) {
  const { profile } = useProfile();
  const { apps } = useAppAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm } = useDialog();

  const isPlatformAdmin = !!profile?.is_admin;
  const isOrgAdmin = !!profile?.is_org_admin;
  const canSeeAdminPanel = isPlatformAdmin || isOrgAdmin;

  let isDirty = false;
  let saveDeal = () => {};
  try {
    const dsCtx = useDebtStudio();
    isDirty = dsCtx.isDirty;
    saveDeal = dsCtx.saveDeal;
  } catch {
    // Sidebar may render outside DebtStudio context
  }

  const appSlugs = (apps || [])
    .map((a) => (typeof a === "string" ? a : a?.slug))
    .filter(Boolean);

  const hasAccess = (slug) => isPlatformAdmin || appSlugs.includes(slug);

  const canUseDebtStudio = hasAccess("debt-studio");
  const canUseFundPlanner = hasAccess("fund-planner");

  const canUseFpStructurer = canUseDebtStudio || canUseFundPlanner;
  const canUseFpFunds = canUseFundPlanner;
  const canUseFpPortfolios = canUseFundPlanner;

  const [openSection, setOpenSection] = useState(null);

  const isPathActive = (basePath) => {
    if (!basePath) return false;
    const normalized = basePath.endsWith("/") ? basePath : `${basePath}/`;
    return (
      location.pathname === basePath || location.pathname.startsWith(normalized)
    );
  };

  const navItems = [
    { label: "Dashboard", icon: <Home size={20} />, to: "/dashboard" },
    {
      label: "MarketMap",
      icon: <Activity size={20} />,
      to: "/market-map",
    },
  ];

  if (hasAccess("trade-tracker")) {
    navItems.push({
      label: "TradeTracker",
      icon: <LineChart size={20} />,
      to: "/trade-tracker",
    });
  }

  if (hasAccess("record-room")) {
    navItems.push({
      label: "RecordRoom",
      icon: <LibraryBig size={20} />,
      to: "/record-room",
    });
  }

  if (canUseDebtStudio) {
    navItems.push({
      label: "DebtStudio",
      icon: <BookOpenCheck size={20} />,
      to: "/debt-studio",
      children: [
        { label: "Issues", to: "/debt-studio/issues" },
        { label: "Portfolios", to: "/debt-studio/portfolios" },
      ],
    });
  }

  const fundPlannerChildren = [];

  if (canUseFpFunds) {
    fundPlannerChildren.push({
      label: "Funds",
      to: "/fund-planner/funds",
    });
  }

  if (canUseFpPortfolios) {
    fundPlannerChildren.push({
      label: "Portfolios",
      to: "/fund-planner/portfolios",
    });
  }

  if (canUseFpStructurer) {
    fundPlannerChildren.push({
      label: "Structurer",
      to: "/fund-planner/structurer",
    });
  }

  if (fundPlannerChildren.length) {
    navItems.push({
      label: "FundPlanner",
      icon: <WalletCards size={20} />,
      to: "/fund-planner",
      children: fundPlannerChildren,
    });
  }

  if (hasAccess("capital-flow")) {
    navItems.push({
      label: "CapitalFlow",
      icon: <Repeat size={20} />,
      to: "/capital-flow",
      children: [
        { label: "Overview", to: "/capital-flow/overview" },
        { label: "Projects", to: "/capital-flow/projects" },
        { label: "Classifications", to: "/capital-flow/classifications" },
        { label: "Debt Allocations", to: "/capital-flow/debt-allocations" },
      ],
    });
  }

  const adminItem = {
    label: "Admin Panel",
    icon: <ShieldCheck size={20} />,
    to: "/admin",
  };

  const settingsItem = {
    label: "Settings",
    icon: <Settings size={20} />,
    to: "/settings",
  };

  const linkBase =
    "flex items-center gap-2 px-2 py-2 rounded-lg transition focus:outline-none focus-visible:ring-app";

  const sidebarWidth = collapsed ? "w-16" : "w-48";
  const labelCls = collapsed ? "hidden" : "inline text-sm md:inline";
  const appsHeaderCls = `uppercase tracking-wider mb-2 text-muted font-semibold text-xs ${
    collapsed ? "hidden" : "block"
  }`;

  const handleNav = async (path) => {
    if (window.localStorage.getItem("compass.inactivityLogout")) {
      navigate(path);
      return;
    }

    const leavingDebtStudio =
      location.pathname.startsWith("/debt-studio") &&
      !path.startsWith("/debt-studio");

    if (leavingDebtStudio && isDirty) {
      const confirmLeave = await confirm(
        "You have unsaved changes in DebtStudio. Save before leaving?",
        {
          title: "Unsaved changes",
          okLabel: "Save",
          cancelLabel: "Stay",
        }
      );

      if (!confirmLeave) return;

      await saveDeal();
    }

    navigate(path);
  };

  const clearAppSessionState = () => {
    Object.keys(sessionStorage).forEach((k) => {
      if (
        k.startsWith("debtstudio.") ||
        k.startsWith("tradetracker.") ||
        k.startsWith("recordroom.") ||
        k.startsWith("marketmap.")
      ) {
        sessionStorage.removeItem(k);
      }
    });

    sessionStorage.removeItem("fundplanner_session_v1");
  };

  const clearRecentsState = () => {
    const knownRecentKeys = ["recent_ds_deals"];

    knownRecentKeys.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        // ignore
      }
    });

    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith("recent_")) {
          localStorage.removeItem(k);
        }
      });
    } catch {
      // ignore
    }
  };

  const handleSignOut = async () => {
    try {
      localStorage.setItem("compass.manualLogout", "1");
      localStorage.removeItem("compass.inactivityLogout");
      localStorage.removeItem("debtstudio.lastSessionBackup");
      sessionStorage.removeItem("debtstudio.session");

      // Clear the inactivity timestamp before any awaited work. Otherwise a
      // pending grace-period callback can race the audit/sign-out requests and
      // turn this explicit logout into an inactivity logout.
      clearActivityState();

      await logAudit({
        category: "auth",
        action: "logout",
        success: true,
        detail: { reason: "user" },
      });
    } catch (err) {
      console.error("Logout audit error:", err);
    } finally {
      // Keep this in the guaranteed cleanup path as well, including browsers
      // where localStorage access or the audit request fails.
      clearActivityState();
      clearAppSessionState();
      clearRecentsState();

      try {
        await supabase.auth.signOut();
      } catch {
        // ignore
      }

      // Always complete an explicit logout at the start page. This also
      // handles signOut failures and auth listeners whose event order varies.
      navigate("/start", { replace: true });
    }
  };

  return (
    <aside
      className={`fixed top-0 left-0 ${sidebarWidth} flex flex-col p-4 gap-2 bg-app text-app h-screen border-r border-app transition-all duration-200 z-40 overflow-hidden`}
      style={{ minWidth: collapsed ? "4rem" : "12rem" }}
    >
      <button
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : undefined}
        className="mb-2 flex items-center justify-center rounded hover:bg-app/10 transition h-10 w-10 shrink-0"
      >
        {collapsed ? <ChevronsRight size={24} /> : <ChevronsLeft size={24} />}
      </button>

      <div className={`${appsHeaderCls} shrink-0`}>Apps</div>

      <div
        className="flex-1 overflow-y-auto pr-1 -mr-1"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <nav className="flex flex-col gap-1">
          {navItems.map(({ label, icon, to, children }) => {
            const hasChildren = Array.isArray(children);
            const isExpanded = openSection === label;

            const isChildActive = children?.some((c) => isPathActive(c.to));
            const isParentActive = isPathActive(to);
            const isRouteActive = isChildActive || isParentActive;

            const handleParentClick = () => {
              if (hasChildren) {
                if (collapsed) {
                  setCollapsed(false);
                  setOpenSection(label);
                  return;
                }

                if (isExpanded) {
                  setOpenSection(null);
                } else {
                  setOpenSection(label);
                }
              } else {
                setOpenSection(null);
                handleNav(to);
              }
            };

            return (
              <div key={label}>
                <button
                  onClick={handleParentClick}
                  aria-label={label}
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  title={collapsed ? label : undefined}
                  className={[
                    linkBase,
                    isRouteActive
                      ? "bg-[hsl(var(--secondary))] text-app font-semibold"
                      : isExpanded
                      ? "bg-[hsl(var(--secondary))/0.35]"
                      : "hover:bg-[hsl(var(--secondary))]",
                  ].join(" ")}
                >
                  <span className="shrink-0">{icon}</span>
                  <span className={`${labelCls} whitespace-nowrap`}>
                    {label}
                  </span>
                  {!collapsed && hasChildren && (
                    <span className="shrink-0" aria-hidden="true">
                      {isExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </span>
                  )}
                </button>

                {!collapsed && hasChildren && isExpanded && (
                  <div className="ml-6 mt-1 flex flex-col gap-1">
                    {children.map(({ label: subLabel, to: subTo }) => {
                      const subActive = isPathActive(subTo);

                      return (
                        <button
                          key={subTo}
                          onClick={() => handleNav(subTo)}
                          className={[
                            "text-xs text-left px-2 py-1 rounded-md transition",
                            subActive
                              ? "text-app font-semibold bg-[hsl(var(--secondary))/0.2]"
                              : "text-muted hover:text-app hover:bg-[hsl(var(--secondary))/0.1]",
                          ].join(" ")}
                        >
                          {subLabel}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="my-3 border-t border-app" />

        <div className="flex flex-col gap-1">
          {canSeeAdminPanel && (
            <button
              onClick={() => handleNav(adminItem.to)}
              aria-label={adminItem.label}
              title={collapsed ? adminItem.label : undefined}
              className={[
                linkBase,
                location.pathname.startsWith("/admin")
                  ? "bg-[hsl(var(--secondary))] text-app font-semibold"
                  : "hover:bg-[hsl(var(--secondary))]",
              ].join(" ")}
            >
              <span className="shrink-0">{adminItem.icon}</span>
              <span className={`${labelCls} whitespace-nowrap`}>
                {adminItem.label}
              </span>
            </button>
          )}

          <button
            onClick={() => handleNav(settingsItem.to)}
            aria-label={settingsItem.label}
            title={collapsed ? settingsItem.label : undefined}
            className={[
              linkBase,
              location.pathname.startsWith("/settings")
                ? "bg-[hsl(var(--secondary))] text-app font-semibold"
                : "hover:bg-[hsl(var(--secondary))]",
            ].join(" ")}
          >
            <span className="shrink-0">{settingsItem.icon}</span>
            <span className={`${labelCls} whitespace-nowrap`}>
              {settingsItem.label}
            </span>
          </button>

          <button
            onClick={handleSignOut}
            aria-label="Sign out"
            title={collapsed ? "Sign out" : undefined}
            className={[
              linkBase,
              "text-muted hover:text-app hover:bg-[hsl(var(--secondary))]",
            ].join(" ")}
          >
            <span className="shrink-0">
              <LogOut size={20} />
            </span>
            <span className={`${labelCls} whitespace-nowrap`}>
              Sign out
            </span>
          </button>
        </div>
      </div>
    </aside>
  );
}
